import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiClock, FiUsers, FiDollarSign, FiCalendar, FiMessageSquare } = FiIcons;

function ROIMetrics() {
  const metrics = [
    { label: 'After-Hours Halifax Leads', value: '127', change: '+23%', icon: FiClock, color: 'blue' },
    { label: 'Service Appointments Booked', value: '89', change: '+45%', icon: FiCalendar, color: 'green' },
    { label: 'CAD Trade Offers', value: '156', change: '+67%', icon: FiDollarSign, color: 'purple' },
    { label: 'Halifax AI Interactions', value: '2,847', change: '+156%', icon: FiMessageSquare, color: 'indigo' }
  ];

  const insights = [
    {
      title: 'Halifax Peak Engagement Hours',
      description: 'Most AI interactions happen between 7-9 PM AST when dealership is closed',
      impact: '34% of Halifax leads generated after hours'
    },
    {
      title: 'Maritime Conversion Rate',
      description: 'AI-assisted Halifax customers are 2.3x more likely to complete a purchase',
      impact: 'Average deal size increased by $2,100 CAD'
    },
    {
      title: 'Atlantic Response Time',
      description: 'AI provides instant responses vs 4-hour average for traditional Halifax inquiries',
      impact: '67% improvement in Maritime customer satisfaction'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Halifax Real-Time ROI Dashboard
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how AI is driving measurable results for Halifax dealership operations with live Maritime performance metrics.
          </p>
        </motion.div>

        {/* Live Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {metrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 text-center"
            >
              <div className={`w-12 h-12 bg-${metric.color}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
                <SafeIcon icon={metric.icon} className={`w-6 h-6 text-${metric.color}-600`} />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</div>
              <div className="text-sm text-gray-600 mb-2">{metric.label}</div>
              <div className={`text-sm font-medium text-${metric.color}-600 flex items-center justify-center space-x-1`}>
                <SafeIcon icon={FiTrendingUp} className="w-3 h-3" />
                <span>{metric.change}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Insights Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-8"
        >
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Halifax AI Performance Insights</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {insights.map((insight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className="border-l-4 border-blue-500 pl-6"
              >
                <h4 className="font-semibold text-gray-900 mb-2">{insight.title}</h4>
                <p className="text-gray-600 text-sm mb-3">{insight.description}</p>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-blue-700 font-medium text-sm">{insight.impact}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Live Activity Feed */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-8 bg-gray-900 rounded-xl p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Live Halifax AI Activity</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 text-sm">Live AST</span>
            </div>
          </div>
          <div className="space-y-4">
            {[
              { time: '2 min ago', action: 'Halifax customer asked about 2023 Audi Q5 Quattro financing', type: 'finance' },
              { time: '5 min ago', action: 'Trade-in estimate completed for Honda Civic (CAD)', type: 'trade' },
              { time: '8 min ago', action: 'Winter tire installation booked at Halifax location', type: 'service' },
              { time: '12 min ago', action: 'AI recommended BMW X3 xDrive for Maritime winter driving', type: 'recommendation' }
            ].map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center space-x-4 p-3 bg-gray-800 rounded-lg"
              >
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'finance' ? 'bg-blue-400' : 
                  activity.type === 'trade' ? 'bg-green-400' : 
                  activity.type === 'service' ? 'bg-yellow-400' : 'bg-purple-400'
                }`}></div>
                <div className="flex-1">
                  <p className="text-gray-300 text-sm">{activity.action}</p>
                </div>
                <span className="text-gray-500 text-xs">{activity.time}</span>
              </motion.div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Powered by <strong>Shawn Ryder Digital Halifax AI</strong> • Processing 500+ Maritime interactions daily
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ROIMetrics;